import React from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, MapPin, Buildings } from "@phosphor-icons/react";
import { AdBanner } from "@/components/AdBanner";

const HERO_BG =
  "https://images.unsplash.com/photo-1513061379709-ef0cd1695189?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjBjaXR5JTIwc2t5bGluZSUyMG5pZ2h0fGVufDB8fHx8MTc4NzczMTI1Nnww&ixlib=rb-4.1.0&q=85";

export const Hero = ({ total, countries, onNearby, onExplore, onAdCta }) => {
  return (
    <section className="relative overflow-hidden border-b border-white/10">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D17]/70 via-[#0B0D17]/85 to-[#0B0D17]" />
      </div>

      <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-10 px-5 py-20 sm:px-8 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl flex-1"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-blue-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-400" />
            The Yellow Pages of Global Franchising
          </span>

          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Every franchise show
            <span className="block text-blue-400">on the planet, in one place.</span>
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            An AI-refreshed directory of franchise &amp; licensing shows across 16+ countries — with deep coverage in
            the US, UK, UAE, Malaysia, Indonesia &amp; India. Verified access. Premium deal intelligence.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-explore-btn"
              onClick={onExplore}
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-500"
            >
              <MagnifyingGlass size={18} weight="bold" />
              Explore Directory
            </button>
            <button
              data-testid="hero-nearby-btn"
              onClick={onNearby}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-white/30 hover:bg-white/10"
            >
              <MapPin size={18} weight="fill" className="text-blue-400" />
              Shows Nearby
            </button>
          </div>

          <div className="mt-10 flex items-center gap-8 text-slate-400">
            <div className="flex items-center gap-2.5">
              <Buildings size={22} weight="duotone" className="text-blue-400" />
              <div>
                <p data-testid="stat-total-shows" className="font-display text-xl font-bold text-white">{total ?? "—"}</p>
                <p className="text-xs uppercase tracking-wider">Live Shows</p>
              </div>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <p className="font-display text-xl font-bold text-white">{countries ?? 6}</p>
              <p className="text-xs uppercase tracking-wider">Countries</p>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <p className="font-display text-xl font-bold text-white">Weekly</p>
              <p className="text-xs uppercase tracking-wider">AI Refresh</p>
            </div>
          </div>
        </motion.div>

        <div className="hidden shrink-0 lg:block">
          <AdBanner onCta={onAdCta} />
        </div>
      </div>
    </section>
  );
};
